/**
 * 외부 JavaScript 파일을 동적으로 로드하는 함수
 * @param {string} src - 로드할 스크립트의 URL
 * @param {Object} [options] - 추가 옵션
 * @param {boolean} [options.async=true] - 비동기 로드 여부
 * @param {boolean} [options.defer=false] - defer 속성 사용 여부
 * @param {string} [options.charset='utf-8'] - 문자 인코딩
 * @param {boolean} [options.removeExisting=true] - 기존 스크립트 제거 여부
 * @returns {Promise<void>} - 스크립트 로드 완료 시 resolve되는 Promise
 */
export function loadScript(src, options = {}) {
    const { async = true, defer = false, charset = 'utf-8', removeExisting = true, parentElement = document.head } = options;

    return new Promise((resolve, reject) => {
        // 이미 로드된 스크립트 확인
        const existingScript = document.querySelector(`script[src="${src}"]`);

        if (existingScript) {
            if (removeExisting) {
                // 기존 스크립트가 로드 중이거나 에러 상태인 경우에만 제거 (readyState 활용)
                if (existingScript.readyState === 'loading' || existingScript.readyState === 'interactive') {
                    parentElement.removeChild(existingScript);
                } else if (existingScript.readyState === 'complete') {
                    // 이미 로드 완료된 경우는 제거하지 않음
                    resolve();
                    return;
                } else {
                    // readyState가 undefined(로드 안됨) 이거나, error 상태라면
                    parentElement.removeChild(existingScript);
                }
            } else {
                // removeExisting이 false이면 바로 resolve
                resolve();
                return;
            }
        }

        // 새 스크립트 엘리먼트 생성
        const script = document.createElement('script');
        script.src = src;
        script.charset = charset;
        script.async = async;
        script.defer = defer;

        // 로드 완료 이벤트 핸들러
        script.onload = () => {
            // console.log(`스크립트 로드 성공: ${src}`);
            resolve();
        };

        // 로드 실패 이벤트 핸들러
        script.onerror = (error) => {
            console.error(`스크립트 로드 실패: ${src}`, error);
            reject(new Error(`스크립트 로드 실패: ${src}`));
        };

        // DOM에 스크립트 추가
        document.head.appendChild(script);
    });
}

/**
 * 여러 스크립트를 순차적으로 로드하는 함수
 * @param {string[]} scripts - 로드할 스크립트 URL 배열
 * @param {Object} [options] - loadScript와 동일한 옵션
 * @returns {Promise<void>} - 모든 스크립트 로드 완료 시 resolve되는 Promise
 */
export async function loadScriptsSequentially(scripts, options) {
    const failedScripts = [];
    for (const src of scripts) {
        try {
            await loadScript(src, options);
        } catch (error) {
            console.error(`Failed to load script: ${src}`, error);
            failedScripts.push(src);
            // Optionally, continue loading other scripts or re-throw the error
        }
    }
    if (failedScripts.length > 0) {
        // Handle the failed scripts (e.g., report to server, inform the user)
        console.warn('Some scripts failed to load:', failedScripts);
    }
}

/**
 * 여러 스크립트를 병렬로 로드하는 함수
 * @param {string[]} scripts - 로드할 스크립트 URL 배열
 * @param {Object} [options] - loadScript와 동일한 옵션
 * @returns {Promise<void>} - 모든 스크립트 로드 완료 시 resolve되는 Promise
 */
export function loadScriptsParallel(scripts, options) {
    return Promise.allSettled(scripts.map((src) => loadScript(src, options))).then((results) => {
        results.forEach((result, index) => {
            if (result.status === 'rejected') {
                console.error(`Failed to load script: ${scripts[index]}`, result.reason);
            }
        });
        // You can also filter the results to get only the successful ones, if needed.
    });
}

/**
 * 스크립트가 이미 로드되었는지 확인하는 함수
 * @param {string} src - 확인할 스크립트의 URL
 * @returns {boolean} - 스크립트 로드 여부
 */
export function isScriptLoaded(src) {
    return !!document.querySelector(`script[src="${src}"]`);
}
